import { Col, DatePicker, Form, Input, message, Modal, Row, Tabs, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { Rule } from "antd/lib/form";
import { partnerApi } from "api/partner.api";
import { AppButton, AppSelect, AppTable } from "components/antd-ui";
import { Checklist } from "components/Checklist/Checklist";
import { NoteLog } from "components/Note/NoteLog";
import { TableContent } from "components/table";
import { fetchStaffOptions, RemoteOption } from "hooks/useRemoteOptions";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { userStore } from "store/userStore";
import { Customer } from "types/customer";
import { ModalStatus } from "types/modal";
import { NoteRefType } from "types/note";
import { Partner } from "types/partner";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";
import { customerIndustryOptions } from "views/Customer/customer.meta";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import {
  getPartnerRelation,
  getPartnerStatus,
  getPartnerTypeLabel,
  PARTNER_PATH,
  partnerRelationOptions,
  partnerStatusOptions,
  partnerTypeOptions,
} from "../partner.meta";
import "views/Customer/Customer.scss";

const requiredRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const emailRules: Rule[] = [{ type: "email", message: "Email không hợp lệ" }];
const phoneRules: Rule[] = [{ pattern: /^[0-9+\s.-]{8,15}$/, message: "Số điện thoại không hợp lệ" }];
const MAX_TEXT = 1000;

interface PartnerForm {
  name: string;
  type: string;
  company?: string;
  position?: string;
  phone?: string;
  email?: string;
  address?: string;
  industry?: string;
  relationLevel: string;
  status: string;
  lastContactDate?: Dayjs;
  staffId?: number;
  bmdHelpsPartner?: string;
  partnerHelpsBmd?: string;
  partnerProducts?: string;
  bmdProducts?: string;
  note?: string;
}

export interface PartnerModal {
  handleCreate: () => void;
  handleUpdate: (partner: Partner) => void;
  handleView: (partner: Partner) => void;
}

interface PartnerModalProps {
  onClose: () => void;
  onSubmitOk: (partner?: Partner) => void;
}

const display = (value?: string | null) => value?.trim() || "--";

const toForm = (partner: Partner): PartnerForm => ({
  name: partner.name,
  type: partner.type,
  company: partner.company,
  position: partner.position,
  phone: partner.phone,
  email: partner.email,
  address: partner.address,
  industry: partner.industry,
  relationLevel: partner.relationLevel,
  status: partner.status,
  lastContactDate: partner.lastContactDate ? dayjs(partner.lastContactDate) : undefined,
  staffId: partner.staff?.id,
  bmdHelpsPartner: partner.bmdHelpsPartner,
  partnerHelpsBmd: partner.partnerHelpsBmd,
  partnerProducts: partner.partnerProducts,
  bmdProducts: partner.bmdProducts,
  note: partner.note,
});

const multiline = (value?: string | null) => (
  <span style={{ whiteSpace: "pre-wrap" }}>{display(value)}</span>
);

const PartnerDetail = ({ partner }: { partner: Partner }) => {
  const relation = getPartnerRelation(partner.relationLevel);
  const status = getPartnerStatus(partner.status);

  return (
    <div>
      <div className="customerModal__section">
        <div className="customerModal__title">Thông tin đối tác</div>
        <div className="customerModal__grid">
          <span className="customerModal__label">Tên</span>
          <span>{display(partner.name)}</span>
          <span className="customerModal__label">Mã</span>
          <span>{display(partner.code)}</span>
          <span className="customerModal__label">Loại</span>
          <span>{getPartnerTypeLabel(partner.type)}</span>
          <span className="customerModal__label">Công ty</span>
          <span>{display(partner.company)}</span>
          <span className="customerModal__label">Chức vụ</span>
          <span>{display(partner.position)}</span>
          <span className="customerModal__label">Số điện thoại</span>
          <span>{display(partner.phone)}</span>
          <span className="customerModal__label">Email</span>
          <span>{display(partner.email)}</span>
          <span className="customerModal__label">Địa chỉ</span>
          <span>{display(partner.address)}</span>
          <span className="customerModal__label">Nhóm ngành nghề</span>
          <span>{display(partner.industry)}</span>
          <span className="customerModal__label">Sale phụ trách</span>
          <span>{display(partner.staff?.name)}</span>
          <span className="customerModal__label">Mức quan hệ</span>
          <span>{relation ? <Tag color={relation.color}>{relation.label}</Tag> : "--"}</span>
          <span className="customerModal__label">Trạng thái</span>
          <span>{status ? <Tag color={status.color}>{status.label}</Tag> : "--"}</span>
          <span className="customerModal__label">Liên hệ gần nhất</span>
          <span>{partner.lastContactDate ? dayjs(partner.lastContactDate).format("DD/MM/YYYY") : "--"}</span>
          <span className="customerModal__label">Ghi chú nội bộ</span>
          {multiline(partner.note)}
        </div>
      </div>

      <div className="customerModal__contacts">
        <div className="customerModal__section">
          <div className="customerModal__title">BMD giúp gì cho đối tác</div>
          {multiline(partner.bmdHelpsPartner)}
        </div>
        <div className="customerModal__section">
          <div className="customerModal__title">Đối tác giúp gì cho BMD</div>
          {multiline(partner.partnerHelpsBmd)}
        </div>
        <div className="customerModal__section">
          <div className="customerModal__title">Sản phẩm đối tác có thể giúp BMD bán</div>
          {multiline(partner.partnerProducts)}
        </div>
        <div className="customerModal__section">
          <div className="customerModal__title">Sản phẩm BMD có thể giúp đối tác bán</div>
          {multiline(partner.bmdProducts)}
        </div>
      </div>
    </div>
  );
};

// Khách do đối tác giới thiệu + tổng hợp số đơn, doanh thu mang về
const PartnerCustomersTab = ({ partner }: { partner: Partner }) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    partnerApi
      .findCustomers(partner.id)
      .then((res) => {
        if (!ignore) setCustomers(res.data.customers || []);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [partner.id]);

  const totalOrders = customers.reduce((sum, customer) => sum + (customer.orderCount || 0), 0);
  const totalAmount = customers.reduce((sum, customer) => sum + (customer.totalAmount || 0), 0);

  const columns = useMemo<TableColumnsType<Customer>>(
    () => [
      { title: "Mã", dataIndex: "code", key: "code", width: 120, render: (value) => <TableContent>{value || "--"}</TableContent> },
      {
        title: "Khách hàng",
        dataIndex: "name",
        key: "name",
        render: (value, record) => <Link to={`/customer?customerId=${record.id}`}>{value || "--"}</Link>,
      },
      {
        title: "Sale chăm sóc",
        key: "staff",
        width: 180,
        render: (_, record) => <TableContent>{record.staff?.name || "--"}</TableContent>,
      },
      {
        title: "Số đơn",
        dataIndex: "orderCount",
        key: "orderCount",
        width: 100,
        align: "right" as const,
        render: (value) => <TableContent>{value || 0}</TableContent>,
      },
      {
        title: "Tổng tiền đã mua",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 180,
        align: "right" as const,
        render: (value) => <TableContent>{formatMoney(value || 0)}</TableContent>,
      },
    ],
    [],
  );

  return (
    <div>
      <div className="flex flex-wrap gap-8 mb-3 font-semibold">
        <span>Số khách giới thiệu: {customers.length}</span>
        <span>Số đơn: {totalOrders}</span>
        <span>Doanh thu mang về: {formatMoney(totalAmount)}</span>
      </div>
      <AppTable<Customer>
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={customers}
        columns={columns}
        locale={{ emptyText: "Chưa có khách hàng nào do đối tác này giới thiệu" }}
      />
    </div>
  );
};

export const PartnerModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: PartnerModalProps, ref) => {
    const [form] = Form.useForm<PartnerForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedPartner, setSelectedPartner] = useState<Partner>();
    const [staffOptions, setStaffOptions] = useState<RemoteOption[]>([]);
    const [activeTab, setActiveTab] = useState("info");

    const onlyMine = !userStore.info.role?.isAdmin && can(PARTNER_PATH, "ONLY_MINE");
    const canUpdate = can(PARTNER_PATH, "UPDATE");

    const fetchStaff = useCallback(
      async (keyword: string) => {
        const list = await fetchStaffOptions(keyword);
        setStaffOptions((prev) => {
          const selectedId = form.getFieldValue("staffId");
          const selected = prev.find((option) => option.value === selectedId);
          return selected && !list.some((option) => option.value === selected.value) ? [selected, ...list] : list;
        });
      },
      [form],
    );

    const debounceSearchStaff = useMemo(() => debounce((keyword: string) => fetchStaff(keyword), 500), [fetchStaff]);

    useEffect(() => () => debounceSearchStaff.cancel(), [debounceSearchStaff]);

    useEffect(() => {
      if (!visible || onlyMine || status === "view") return;
      fetchStaff("");
    }, [visible, onlyMine, status, fetchStaff]);

    const fillPartner = useCallback(
      (partner: Partner) => {
        form.setFieldsValue(toForm(partner));
        setStaffOptions(
          partner.staff ? [{ label: partner.staff.name || partner.staff.username, value: partner.staff.id }] : [],
        );
        setSelectedPartner(partner);
      },
      [form],
    );

    useImperativeHandle<any, PartnerModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          form.setFieldsValue({ type: "PERSON", relationLevel: "NORMAL", status: "ACTIVE" });
          setStaffOptions([]);
          setSelectedPartner(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(partner: Partner) {
          fillPartner(partner);
          setStatus("update");
          setVisible(true);
        },
        handleView(partner: Partner) {
          fillPartner(partner);
          setActiveTab("info");
          setStatus("view");
          setVisible(true);
        },
      }),
      [fillPartner, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedPartner(undefined);
      onClose();
    }, [onClose]);

    const handleStartEdit = useCallback(() => {
      setActiveTab("info");
      setStatus("update");
    }, []);

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        partner: {
          name: (values.name || "").trim(),
          type: values.type || "PERSON",
          company: values.company || "",
          position: values.position || "",
          phone: (values.phone || "").trim(),
          email: (values.email || "").trim(),
          address: values.address || "",
          industry: (values.industry || "").trim(),
          relationLevel: values.relationLevel || "NORMAL",
          status: values.status || "ACTIVE",
          lastContactDate: values.lastContactDate ? values.lastContactDate.format("YYYY-MM-DD") : "",
          bmdHelpsPartner: values.bmdHelpsPartner || "",
          partnerHelpsBmd: values.partnerHelpsBmd || "",
          partnerProducts: values.partnerProducts || "",
          bmdProducts: values.bmdProducts || "",
          note: values.note || "",
        },
        staffId: onlyMine ? undefined : values.staffId ?? 0,
      };
    }, [form, onlyMine]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        const res =
          status === "create" ? await partnerApi.create(data) : await partnerApi.update(selectedPartner?.id || 0, data);
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk(res.data);
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedPartner?.id, status]);

    const title = status === "create" ? "Tạo mới đối tác" : status === "update" ? "Cập nhật đối tác" : "Xem đối tác";

    const textArea = (label: string, name: keyof PartnerForm, placeholder: string) => (
      <Col span={12}>
        <Form.Item label={label} name={name} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
          <Input.TextArea rows={4} maxLength={MAX_TEXT} showCount placeholder={placeholder} />
        </Form.Item>
      </Col>
    );

    return (
      <Modal
        className="customerModal"
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={1200}
        confirmLoading={loading}
        footer={
          status === "view" ? (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {canUpdate && (
                <AppButton tone="primary" onClick={handleStartEdit}>
                  Sửa
                </AppButton>
              )}
            </div>
          ) : (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Hủy</AppButton>
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            </div>
          )
        }
      >
        {status === "view" && selectedPartner ? (
          <Tabs
            key={selectedPartner.id}
            className="customerModal__tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "info", label: "Thông tin đối tác", children: <PartnerDetail partner={selectedPartner} /> },
              { key: "customers", label: "Khách giới thiệu", children: <PartnerCustomersTab partner={selectedPartner} /> },
              {
                key: "note",
                label: "Ghi chú",
                children: <NoteLog refType={NoteRefType.Partner} refId={selectedPartner.id} />,
              },
              {
                key: "todo",
                label: "Việc cần làm",
                children: <Checklist refType="PARTNER" refId={selectedPartner.id} disabled={!canUpdate} />,
              },
            ]}
          />
        ) : (
          <Form
            className="customerModal__form"
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: "140px" }}
            colon={false}
            form={form}
          >
            <div className="customerModal__section">
              <div className="customerModal__title">Thông tin đối tác</div>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item label="Tên" name="name" rules={requiredRules}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Loại" name="type">
                    <AppSelect options={partnerTypeOptions} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Nhóm ngành nghề"
                    name="industry"
                    getValueProps={(value?: string) => ({ value: value ? [value] : [] })}
                    normalize={(value?: string[]) => value?.[value.length - 1] || ""}
                  >
                    <AppSelect
                      mode="tags"
                      maxCount={1}
                      allowClear
                      options={customerIndustryOptions}
                      placeholder="Chọn hoặc nhập ngành nghề"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Công ty" name="company">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Chức vụ" name="position">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Số điện thoại" name="phone" rules={phoneRules}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Email" name="email" rules={emailRules}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Địa chỉ" name="address">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {onlyMine ? (
                    <Form.Item label="Sale phụ trách">
                      <Input value={userStore.info.name || ""} disabled />
                    </Form.Item>
                  ) : (
                    <Form.Item label="Sale phụ trách" name="staffId">
                      <AppSelect
                        showSearch
                        allowClear
                        filterOption={false}
                        options={staffOptions}
                        onSearch={debounceSearchStaff}
                        placeholder="Chọn nhân viên"
                      />
                    </Form.Item>
                  )}
                </Col>
                <Col span={12}>
                  <Form.Item label="Mức quan hệ" name="relationLevel">
                    <AppSelect options={partnerRelationOptions} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Trạng thái" name="status">
                    <AppSelect options={partnerStatusOptions} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Liên hệ gần nhất" name="lastContactDate">
                    <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Ghi chú nội bộ" name="note">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="customerModal__section">
              <div className="customerModal__title">Hợp tác hai bên</div>
              <Row gutter={24}>
                {textArea("BMD giúp gì cho đối tác", "bmdHelpsPartner", "VD: cung cấp giải pháp, giới thiệu khách...")}
                {textArea("Đối tác giúp gì cho BMD", "partnerHelpsBmd", "VD: giới thiệu khách, kết nối nhà máy...")}
                {textArea("Sản phẩm đối tác có thể giúp BMD bán", "partnerProducts", "Ghi tự do: sản phẩm, điều kiện...")}
                {textArea("Sản phẩm BMD có thể giúp đối tác bán", "bmdProducts", "Ghi tự do: sản phẩm, điều kiện...")}
              </Row>
            </div>
          </Form>
        )}
      </Modal>
    );
  }),
);
